import React from "react";
import styles from "./PartialYmmvCard.module.css";

/**
 * TRAC-896 (ruled 8/12): the customer's finished picker rendered as a message
 * FROM THEM — the same card language as the Find Your Vehicle card the agent
 * raised, but sitting in the user's slot carrying their answer: the vehicle,
 * and the tire size when one was chosen (a fitment QUESTION commits no size,
 * so none is shown for it).
 *
 * Purely presentational and inert by construction — it is a record of a choice
 * already sent to the agent, not a control. Tap nothing, morph nothing, hover
 * nothing. Deliberately shares PartialYmmvCard's stylesheet so the two cards
 * cannot drift apart visually.
 */

interface YmmvSelectionCardProps {
  vehicle: string;
  tireSize?: string;
}

/**
 * A staggered pick arrives as one string — "275/35R22 (front) / 315/30R22
 * (rear)", the same shape handleTireSizeSelect parses — and reads better as
 * "F: x  R: y" on one line (wrapping only when the card is too narrow) than
 * as a 40-character run. Same regex as the completion path.
 * Anything that doesn't match (square sizes, flotation sizes like
 * "35X12.50R17") is already a single token and renders as-is.
 */
const STAGGERED_RE = /^(\S+)\s*\(front\)\s*\/\s*(\S+)\s*\(rear\)$/i;

const YmmvSelectionCard: React.FC<YmmvSelectionCardProps> = ({ vehicle, tireSize }) => {
  const staggered = tireSize ? STAGGERED_RE.exec(tireSize) : null;
  return (
    <div
      className={`${styles.card} ${styles.selectionCard}`}
      role="img"
      aria-label={
        staggered
          ? `Selected ${vehicle}, front tire size ${staggered[1]}, rear tire size ${staggered[2]}`
          : tireSize
            ? `Selected ${vehicle}, tire size ${tireSize}`
            : `Selected ${vehicle}`
      }
      data-track-id="ymmv_selection_card"
    >
      <div className={styles.inner}>
        <div className={styles.searchPill}>
          <svg
            className={`${styles.searchIcon} ${styles.checkIcon}`}
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className={styles.searchValue}>{vehicle}</span>
        </div>
        {staggered ? (
          <div className={`${styles.selectionSize} ${styles.selectionSizeStaggered}`}>
            <span>F: {staggered[1]}</span>
            <span>R: {staggered[2]}</span>
          </div>
        ) : tireSize ? (
          <div className={styles.selectionSize}>{tireSize}</div>
        ) : null}
      </div>
    </div>
  );
};

export default YmmvSelectionCard;
